import React, { Fragment, FunctionComponent, useState } from "react";
import { connect, MapStateToProps } from "react-redux";

import { Caption, Display, Layout, MessageWindow, SpacingSize } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { IAppState, NoneOwnProps } from "@multiweb/types/react";
import { createTriggerCopy, getDefaultTriggerState } from "../../../../creatorMethodsUtils"; // kvůli prevenci vůči cyklické závislosti je nutné importovat z creatorMethods a ne přes utils
import { TriggerType } from "../../../../enums";
import { LOC_ATTRIBUTE_VALUE_CHANGE, LOC_CANCEL, LOC_CREATION_OF_DOCUMENT, LOC_DELETE, LOC_DELETE_SELECTED_TRIGGER, LOC_MENU, LOC_NO_TRIGGER_CREATED, LOC_TRIGGER_TYPE, LOC_WORKFLOW_MESSAGE } from "../../../../locKeys";
import { ITrigger, WorkflowEditorProps } from "../../../../types";
import { SideListFrame, SidePanelItemsSource, WfOption, WfSelect } from "../../components";
import { triggerPageRenderer } from "./utils";

type TriggersTabComponentProps = {
    readonly triggers: readonly ITrigger[];
    readonly selectedRecordIndex: number;
    readonly updateTriggers: (newTriggers: readonly ITrigger[]) => void;
    readonly noTriggerCreatedText: string;
    readonly deleteSelectedTriggerText: string;
} & WorkflowEditorProps;

const TriggersTabComponent: FunctionComponent<TriggersTabComponentProps> = ({ hostKey, triggers, selectedRecordIndex, updateTriggers, noTriggerCreatedText, deleteSelectedTriggerText }) => {
    const [selectedTriggerIndex, setSelectedTriggerIndex] = useState(selectedRecordIndex);
    const [isMessageWindowShown, setIsMessageWindowShown] = useState(false);
    const selectedTrigger = triggers[selectedTriggerIndex];

    const actionsItemSource: readonly SidePanelItemsSource[] = triggers.map((trigger, index) => {
        return { headerLocKey: trigger.locKey, description1: trigger.name, iconName: trigger.properties.iconName, onItemClick: () => setSelectedTriggerIndex(index) };
    });

    const onCloseMessageWindow = () => {
        setIsMessageWindowShown(false);
    };

    const changeTriggerType = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newTriggers = [...triggers];
        const triggerType: TriggerType = e.target.value as TriggerType;
        newTriggers[selectedTriggerIndex] = getDefaultTriggerState(triggerType);
        updateTriggers(newTriggers);
    };

    const addTrigger = () => {
        const newTriggers = [...triggers, getDefaultTriggerState(TriggerType.Menu)];
        updateTriggers(newTriggers);
        setSelectedTriggerIndex(newTriggers.length - 1);
    };

    const removeTrigger = () => {
        updateTriggers(triggers.filter((_trigger, index) => index !== selectedTriggerIndex));
        setSelectedTriggerIndex(selectedTriggerIndex - 1 >= 0 ? selectedTriggerIndex - 1 : 0);
    };

    const copyTrigger = () => {
        const newTriggers = [...triggers];
        newTriggers.splice(selectedTriggerIndex + 1, 0, createTriggerCopy(selectedTrigger));
        updateTriggers(newTriggers);
        setSelectedTriggerIndex(selectedTriggerIndex + 1);
    };

    const updateSelectedTrigger = (valueKeyPairs: readonly { key: string; value: string }[]) => {
        const newTriggers = [...triggers];
        const newProperties = { ...selectedTrigger.properties };

        for (const { key, value } of valueKeyPairs)
            newProperties[key] = value;

        newTriggers[selectedTriggerIndex] = { ...selectedTrigger, properties: newProperties };
        updateTriggers(newTriggers);
    };

    const changeSelectedTriggerOrder = (newIndex: number) => {
        const newTriggers = [...triggers];
        newTriggers[selectedTriggerIndex] = newTriggers[newIndex];
        newTriggers[newIndex] = selectedTrigger;
        updateTriggers(newTriggers);
        setSelectedTriggerIndex(newIndex);
    };

    const handleDeleteButtonClick = () => {
        setIsMessageWindowShown(true);
    };

    return (
        <Fragment>
            <SideListFrame
                hostKey={hostKey}
                selectedItemIndex={selectedTriggerIndex}
                itemsSource={actionsItemSource}
                onAddItemClick={addTrigger}
                onRemoveItemClick={handleDeleteButtonClick}
                onCopyItemClick={copyTrigger}
                onOrderChange={changeSelectedTriggerOrder}>
                {selectedTrigger ?
                    <Fragment>
                        <Layout spacingSizeBottom={SpacingSize.m} display={Display.block}>
                            <Caption locKey={LOC_TRIGGER_TYPE} />
                            <WfSelect value={selectedTrigger.triggerType} onChange={changeTriggerType} {...{ hostKey }}>
                                <WfOption value={TriggerType.AttributeDataChange} locKey={LOC_ATTRIBUTE_VALUE_CHANGE} />
                                <WfOption value={TriggerType.Menu} locKey={LOC_MENU} />
                                <WfOption value={TriggerType.NewObject} locKey={LOC_CREATION_OF_DOCUMENT} />
                                <WfOption value={TriggerType.WfMessage} locKey={LOC_WORKFLOW_MESSAGE} />
                            </WfSelect>
                        </Layout>
                        {triggerPageRenderer(hostKey, selectedTrigger, updateSelectedTrigger)}
                    </Fragment> :
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span>{noTriggerCreatedText}</span>
                    </div>}
            </SideListFrame>
            {isMessageWindowShown &&
                <MessageWindow
                    primaryButtonLocKey={LOC_DELETE}
                    secondaryButtonLocKey={LOC_CANCEL}
                    onPrimaryButtonClick={removeTrigger}
                    {...{ onCloseMessageWindow }}
                >
                    {deleteSelectedTriggerText}
                </MessageWindow>}
        </Fragment>
    );
};

type StateProps = Pick<TriggersTabComponentProps, "noTriggerCreatedText" | "deleteSelectedTriggerText">;

const mapStateToProps: MapStateToProps<StateProps, NoneOwnProps, IAppState> = (state) => ({
    noTriggerCreatedText: getPrimaryText(state, LOC_NO_TRIGGER_CREATED) ?? "Žádný spouštěč zatím nebyl vytvořen...",
    deleteSelectedTriggerText: getPrimaryText(state, LOC_DELETE_SELECTED_TRIGGER) ?? "Opravdu chcete smazat vybraný spouštěč?",
});

export const TriggersTab = connect(mapStateToProps)(TriggersTabComponent);
